import { useCallback } from 'react';
import {
  useNavigate as useReactRouterNavigate,
  useLocation,
  useParams,
  type NavigateOptions,
  type To,
} from 'react-router-dom';
import i18n, {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
} from './index';

const PREFIXED_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (l) => l !== DEFAULT_LANGUAGE
);

const LANG_PREFIX_RE = new RegExp(`^/(${PREFIXED_LANGUAGES.join('|')})(?=/|$)`);

/**
 * Retire le préfixe de langue d'un chemin si présent.
 * Ex: "/en/retour" -> "/retour", "/retour" -> "/retour"
 */
export function stripLangPrefix(path: string): string {
  return path.replace(LANG_PREFIX_RE, '') || '/';
}

/**
 * Préfixe un chemin avec la langue donnée (FR = pas de préfixe).
 * Idempotent : si le path est déjà préfixé pour cette langue, ne double pas.
 */
export function localizedPath(path: string, lang?: SupportedLanguage): string {
  const target = (lang ?? (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE);
  const clean = stripLangPrefix(path.startsWith('/') ? path : '/' + path);
  if (target === DEFAULT_LANGUAGE) return clean;
  return `/${target}${clean === '/' ? '' : clean}` || `/${target}`;
}

/**
 * Lit la langue actuelle depuis le premier segment de l'URL si présent,
 * sinon retourne null (le LanguageDetector standard prendra le relais).
 */
export function readLangFromPath(pathname: string): SupportedLanguage | null {
  const seg = pathname.split('/')[1];
  if (seg && (SUPPORTED_LANGUAGES as readonly string[]).includes(seg)) {
    return seg as SupportedLanguage;
  }
  return null;
}

/**
 * Wrapper de useNavigate : préfixe automatiquement les chemins avec la langue active.
 * À utiliser comme drop-in remplacement de useNavigate de react-router-dom.
 */
export function useNavigate() {
  const navigate = useReactRouterNavigate();
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      if (typeof to === 'string') {
        navigate(localizedPath(to), options);
        return;
      }
      // Objet { pathname, search, hash }
      const pathname = to.pathname ? localizedPath(to.pathname) : to.pathname;
      navigate({ ...to, pathname }, options);
    },
    [navigate]
  );
}

/**
 * Hook : retourne la langue active (depuis l'URL ou i18n).
 */
export function useCurrentLanguage(): SupportedLanguage {
  const params = useParams();
  const fromParam = params.lang as SupportedLanguage | undefined;
  if (fromParam && (SUPPORTED_LANGUAGES as readonly string[]).includes(fromParam)) {
    return fromParam;
  }
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
}

/**
 * Hook : retourne l'URL équivalente dans une autre langue (pour le switcher).
 */
export function useUrlForLanguage(): (lang: SupportedLanguage) => string {
  const location = useLocation();
  return useCallback(
    (lang) => {
      const base = localizedPath(location.pathname, lang);
      return base + location.search + location.hash;
    },
    [location.pathname, location.search, location.hash]
  );
}
